import { copy } from "../copy";
import { formatMoney } from "../format";
import type { LandingWin } from "../feed";
import Icon from "./Icon";

// Producers only: the feed carries no backer usernames, so there is no
// "top backers" column.
export default function WinnersWall({ winners }: { winners: LandingWin[] }) {
  // A winner without a payout figure has nothing to show on a wall of payouts.
  const paid = winners.filter((w) => (w.payout_cents ?? 0) > 0);
  if (paid.length === 0) return null;
  const topCents = Math.max(...paid.map((w) => w.payout_cents!));
  const [lead, tail] = copy.winners.heading;

  return (
    <section
      id="winners"
      data-testid="winners-wall"
      className="border-t border-ground-edge px-4 py-20 md:px-6 md:py-28"
    >
      <div className="mx-auto w-full max-w-[1240px]">
        <p className="text-[11px] uppercase tracking-[0.24em] text-chrome-dim">{copy.winners.eyebrow}</p>
        <h2 className="mt-3 font-headline text-4xl uppercase leading-[0.95] tracking-[-0.01em] text-chrome md:text-6xl">
          {lead} <span className="text-gold">{tail}</span>
        </h2>
        <h3 className="mt-12 text-[11px] uppercase tracking-[0.2em] text-chrome-dim">{copy.winners.list}</h3>
        <ul className="mt-5 grid grid-cols-1 gap-3 lg:grid-cols-2">
          {paid.map((w, i) => {
            const isTop = i === paid.findIndex((p) => p.payout_cents === topCents);
            return (
              <li
                key={`${w.producer_username}:${w.contest_name}:${w.at}`}
                data-testid="winner"
                className={`flex items-center gap-4 rounded-xl border p-4 transition-colors ${
                  isTop ? "border-gold/40 bg-gold/5" : "border-ground-edge bg-ground-raised hover:border-chrome-dim/40"
                }`}
              >
                <span className="flex w-8 items-center justify-center">
                  {isTop ? (
                    <span
                      title={copy.winners.top}
                      className="flex h-8 w-8 items-center justify-center rounded-full bg-gold/15 text-gold"
                    >
                      <Icon name="trophy" className="text-lg" />
                    </span>
                  ) : (
                    <span className="bf-tabular text-sm font-bold text-chrome-dim">{String(i + 1).padStart(2, "0")}</span>
                  )}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-bold text-chrome">@{w.producer_username}</p>
                  <p className="truncate text-xs text-chrome-dim">{w.contest_name}</p>
                </div>
                <p className="bf-tabular font-headline text-2xl leading-none text-gold md:text-3xl">
                  {formatMoney(w.payout_cents!)}
                </p>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
